// src/pages/labita/total-stok-lahan/TotalStokLahanNF.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Stack,
    Snackbar,
    Tabs,
    Tab,
    Grid,
    Chip,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find(lokasi => lokasi.id_lokasi === id_lokasi);
};

// Daftar aktifitas yang ditentukan
const AKTIFITAS_LIST = [
    "Siap BK",
    "Cabut Nanas",
    "BK",
    "Chop",
    "Timbun",
    "MB",
    "Chelly",
    "DP",
    "Sub-Soil",
    "FHK",
    "P. Harrow",
    "F-R",
    "Ridger",
    "Buat Jln",
    "Sal. Ter.",
    "Sal. Sek.",
    "Siap Tanam",
    "Tanam"
];

// Fungsi untuk parse nilai numerik dari string
const parseNumber = (value) => {
    if (!value || value === '-' || value === '') {
        return 0;
    }
    
    try {
        // Hapus karakter non-digit kecuali koma dan titik
        const cleaned = value.toString().replace(/[^\d.,]/g, '');
        // Ganti koma dengan titik
        const normalized = cleaned.replace(',', '.');
        const parsed = parseFloat(normalized);
        
        return isNaN(parsed) ? 0 : parsed;
    } catch (error) {
        console.error("Error parsing number:", error);
        return 0;
    }
};

// Fungsi untuk format angka dengan pemisah ribuan
const formatNumberWithSeparator = (number) => {
    if (number === 0) return '0';
    return number.toLocaleString('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
};

// Fungsi untuk menghitung data chart berdasarkan formula yang diberikan
const calculateChartData = (summaryDataNF, summaryDataPS) => {
    // Helper function untuk menghitung data per komoditas
    const calculateChartDataForCommodity = (summaryData) => {
        // Buat objek untuk memudahkan akses data berdasarkan aktifitas
        const dataByAktifitas = {};
        summaryData.forEach(item => {
            if (item.aktifitas !== "TOTAL") {
                dataByAktifitas[item.aktifitas] = item;
            }
        });

        // Formula 1: Siap Tanam = Real Siap Tanam + Sisa Tanam
        const siapTanamReal = dataByAktifitas["Siap Tanam"]?.real || 0;
        const sisaTanam = dataByAktifitas["Tanam"]?.sisa || 0;
        const siapTanamValue = siapTanamReal + sisaTanam;

        // Formula 2: Selesai Ridger = Real Ridger + Netto Buat Jln + Netto Sal. Ter. + Netto Sal. Sek.
        const realRidger = dataByAktifitas["Ridger"]?.real || 0;
        const nettoBuatJln = dataByAktifitas["Buat Jln"]?.netto || 0;
        const nettoSalTer = dataByAktifitas["Sal. Ter."]?.netto || 0;
        const nettoSalSek = dataByAktifitas["Sal. Sek."]?.netto || 0;
        const selesaiRidgerValue = realRidger + nettoBuatJln + nettoSalTer + nettoSalSek;

        // Formula 3: Selesai Bajak = Real MB + Real Chelly + Netto DP + Netto Sub-Soil + Netto FHK + Netto P. Harrow + Netto F-R + Sisa Ridger
        const realMB = dataByAktifitas["MB"]?.real || 0;
        const realChelly = dataByAktifitas["Chelly"]?.real || 0;
        const nettoDP = dataByAktifitas["DP"]?.netto || 0;
        const nettoSubSoil = dataByAktifitas["Sub-Soil"]?.netto || 0;
        const nettoFHK = dataByAktifitas["FHK"]?.netto || 0;
        const nettoPHarrow = dataByAktifitas["P. Harrow"]?.netto || 0;
        const nettoFR = dataByAktifitas["F-R"]?.netto || 0;
        const sisaRidger = dataByAktifitas["Ridger"]?.sisa || 0;
        const selesaiBajakValue = realMB + realChelly + nettoDP + nettoSubSoil + nettoFHK + nettoPHarrow + nettoFR + sisaRidger;

        // Formula 4: Selesai Chopper = Real Chop + Netto Timbun + Sisa MB + Sisa Chelly
        const realChop = dataByAktifitas["Chop"]?.real || 0;
        const nettoTimbun = dataByAktifitas["Timbun"]?.netto || 0;
        const sisaMB = dataByAktifitas["MB"]?.sisa || 0;
        const sisaChelly = dataByAktifitas["Chelly"]?.sisa || 0;
        const selesaiChopperValue = realChop + nettoTimbun + sisaMB + sisaChelly;

        // Formula 5: Siap Bongkar = Real Siap BK + Netto Cabut Nanas + Netto BK + Sisa Chop
        const realSiapBK = dataByAktifitas["Siap BK"]?.real || 0;
        const nettoCabutNanas = dataByAktifitas["Cabut Nanas"]?.netto || 0;
        const nettoBK = dataByAktifitas["BK"]?.netto || 0;
        const sisaChop = dataByAktifitas["Chop"]?.sisa || 0;
        const siapBongkarValue = realSiapBK + nettoCabutNanas + nettoBK + sisaChop;

        return {
            siapTanamValue,
            selesaiRidgerValue,
            selesaiBajakValue,
            selesaiChopperValue,
            siapBongkarValue
        };
    };

    // Hitung data untuk NF
    const dataNF = calculateChartDataForCommodity(summaryDataNF);
    
    // Hitung data untuk PS (Banana)
    const dataPS = calculateChartDataForCommodity(summaryDataPS);

    // Data untuk chart dengan kedua komoditas
    const chartDataset = [
        {
            category: "Siap Tanam",
            NF: dataNF.siapTanamValue,
            PS: dataPS.siapTanamValue,
        },
        {
            category: "Selesai Ridger",
            NF: dataNF.selesaiRidgerValue,
            PS: dataPS.selesaiRidgerValue,
        },
        {
            category: "Selesai Bajak",
            NF: dataNF.selesaiBajakValue,
            PS: dataPS.selesaiBajakValue,
        },
        {
            category: "Selesai Chopper",
            NF: dataNF.selesaiChopperValue,
            PS: dataPS.selesaiChopperValue,
        },
        {
            category: "Siap Bongkar",
            NF: dataNF.siapBongkarValue,
            PS: dataPS.siapBongkarValue,
        },
    ];

    return chartDataset;
};

// Fungsi untuk menghitung total stok lahan bongkar per komoditas
export const calculateTotalStokLahanBongkar = (summaryDataNF, summaryDataPS) => {
    const calculateTotalForCommodity = (summaryData) => {
        // Buat objek untuk memudahkan akses data berdasarkan aktifitas
        const dataByAktifitas = {};
        summaryData.forEach(item => {
            if (item.aktifitas !== "TOTAL") {
                dataByAktifitas[item.aktifitas] = item;
            }
        });

        // Formula 2: Selesai Ridger = Real Ridger + Netto Buat Jln + Netto Sal. Ter. + Netto Sal. Sek.
        const realRidger = dataByAktifitas["Ridger"]?.real || 0;
        const nettoBuatJln = dataByAktifitas["Buat Jln"]?.netto || 0;
        const nettoSalTer = dataByAktifitas["Sal. Ter."]?.netto || 0;
        const nettoSalSek = dataByAktifitas["Sal. Sek."]?.netto || 0;
        const selesaiRidgerValue = realRidger + nettoBuatJln + nettoSalTer + nettoSalSek;

        // Formula 3: Selesai Bajak = Real MB + Real Chelly + Netto DP + Netto Sub-Soil + Netto FHK + Netto P. Harrow + Netto F-R + Sisa Ridger
        const realMB = dataByAktifitas["MB"]?.real || 0;
        const realChelly = dataByAktifitas["Chelly"]?.real || 0;
        const nettoDP = dataByAktifitas["DP"]?.netto || 0;
        const nettoSubSoil = dataByAktifitas["Sub-Soil"]?.netto || 0;
        const nettoFHK = dataByAktifitas["FHK"]?.netto || 0;
        const nettoPHarrow = dataByAktifitas["P. Harrow"]?.netto || 0;
        const nettoFR = dataByAktifitas["F-R"]?.netto || 0;
        const sisaRidger = dataByAktifitas["Ridger"]?.sisa || 0;
        const selesaiBajakValue = realMB + realChelly + nettoDP + nettoSubSoil + nettoFHK + nettoPHarrow + nettoFR + sisaRidger;

        // Formula 4: Selesai Chopper = Real Chop + Netto Timbun + Sisa MB + Sisa Chelly
        const realChop = dataByAktifitas["Chop"]?.real || 0;
        const nettoTimbun = dataByAktifitas["Timbun"]?.netto || 0;
        const sisaMB = dataByAktifitas["MB"]?.sisa || 0;
        const sisaChelly = dataByAktifitas["Chelly"]?.sisa || 0;
        const selesaiChopperValue = realChop + nettoTimbun + sisaMB + sisaChelly;

        // Formula 5: Siap Bongkar = Real Siap BK + Netto Cabut Nanas + Netto BK + Sisa Chop
        const realSiapBK = dataByAktifitas["Siap BK"]?.real || 0;
        const nettoCabutNanas = dataByAktifitas["Cabut Nanas"]?.netto || 0;
        const nettoBK = dataByAktifitas["BK"]?.netto || 0;
        const sisaChop = dataByAktifitas["Chop"]?.sisa || 0;
        const siapBongkarValue = realSiapBK + nettoCabutNanas + nettoBK + sisaChop;

        // Total Stok Lahan Bongkar = Selesai Ridger + Selesai Bajak + Selesai Chopper + Siap Bongkar
        const totalStokLahanBongkar = selesaiRidgerValue + selesaiBajakValue + selesaiChopperValue + siapBongkarValue;

        return {
            selesaiRidgerValue,
            selesaiBajakValue,
            selesaiChopperValue,
            siapBongkarValue,
            totalStokLahanBongkar
        };
    };

    // Hitung untuk NF (Fresh Pine)
    const dataNF = calculateTotalForCommodity(summaryDataNF);
    
    // Hitung untuk PS (Banana)
    const dataPS = calculateTotalForCommodity(summaryDataPS);

    return {
        freshPine: dataNF.totalStokLahanBongkar,
        banana: dataPS.totalStokLahanBongkar,
        freshPineDetail: dataNF,
        bananaDetail: dataPS
    };
};

// Value formatter untuk menampilkan angka dengan pemisah ribuan
const valueFormatter = (value) => `${formatNumberWithSeparator(value)} Ha`;

function TotalStokLahanNF({ onTotalStokLahanBongkarChange }) {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();

    const [dataListNF, setDataListNF] = useState([]);
    const [dataListPS, setDataListPS] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [activeTab, setActiveTab] = useState(0); // 0 = Chart, 1 = Fresh Pine, 2 = Banana
    const [chartData, setChartData] = useState([]);

    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // State untuk total keseluruhan
    const [totalNettoNF, setTotalNettoNF] = useState(0);
    const [totalRealNF, setTotalRealNF] = useState(0);
    const [totalSisaNF, setTotalSisaNF] = useState(0);
    
    const [totalNettoPS, setTotalNettoPS] = useState(0);
    const [totalRealPS, setTotalRealPS] = useState(0);
    const [totalSisaPS, setTotalSisaPS] = useState(0);

    // State untuk total stok lahan bongkar
    const [totalStokLahanBongkar, setTotalStokLahanBongkar] = useState({
        freshPine: 0,
        banana: 0
    });

    // Fungsi untuk handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_labita/tb_stok_lahan
                const stokLahanRef = ref(database, "db_labita/tb_stok_lahan");
                const stokLahanSnapshot = await onValuePromise(stokLahanRef);
                const stokLahanData = stokLahanSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (stokLahanData) {
                    Object.keys(stokLahanData).forEach(key => {
                        const record = stokLahanData[key];
                        loadedData.push({ 
                            firebaseKey: key, 
                            id_lokasi: record.id_lokasi || '',
                            ex_comodity: record.ex_comodity || '',
                            real: record.real || '',
                            interval: record.interval || '',
                            alokasi: record.alokasi || '',
                            aktifitas: record.aktifitas || '',
                        });
                        
                        // Cari waktu update terakhir dari last_imported_at
                        if (record.last_imported_at) {
                            const updateTime = new Date(record.last_imported_at);
                            if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                latestUpdateTime = updateTime;
                            }
                        }
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Fetch data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();
                
                let lokasiArray = [];
                if (lokasiRawData) {
                    // Konversi dari object Firebase ke array
                    lokasiArray = Object.keys(lokasiRawData).map(key => ({
                        firebaseKey: key,
                        ...lokasiRawData[key]
                    }));
                    
                    // Update dataList dengan informasi dari tb_lokasi
                    const updatedDataList = loadedData.map((item) => {
                        const lokasiInfo = findLokasiById(lokasiArray, item.id_lokasi);
                        
                        if (lokasiInfo) {
                            // Jika ditemukan di tb_lokasi
                            const displayLokasi = lokasiInfo.lokasi || item.id_lokasi;
                            const displayLuasNetto = lokasiInfo.netto || '-';
                            const displayStatus = lokasiInfo.status || '';
                            
                            return {
                                ...item,
                                lokasi: displayLokasi,
                                luas_netto: displayLuasNetto,
                                status: displayStatus,
                                tahun: lokasiInfo.tahun || '',
                            };
                        } else {
                            // Jika TIDAK ditemukan di tb_lokasi
                            return {
                                ...item,
                                lokasi: '-',
                                luas_netto: '-',
                                status: '',
                                tahun: '',
                            };
                        }
                    });
                    
                    setOriginalDataList(updatedDataList);
                    
                    // Hitung total berdasarkan aktifitas untuk NF dan PS
                    calculateTotals(updatedDataList);
                } else {
                    // Jika tb_lokasi kosong, gunakan data dasar
                    const basicDataList = loadedData.map((item) => {
                        return {
                            ...item,
                            lokasi: item.id_lokasi || '',
                            luas_netto: '-',
                            status: '',
                            tahun: '',
                        };
                    });
                    
                    setOriginalDataList(basicDataList);
                    
                    // Hitung total berdasarkan aktifitas untuk NF dan PS
                    calculateTotals(basicDataList);
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Fungsi untuk menghitung total berdasarkan aktifitas
    const calculateTotals = (data) => {
        const summaryDataNF = [];
        const summaryDataPS = [];
        
        let totalNettoSumNF = 0;
        let totalRealSumNF = 0;
        let totalSisaSumNF = 0;
        
        let totalNettoSumPS = 0;
        let totalRealSumPS = 0;
        let totalSisaSumPS = 0;

        // Hitung untuk setiap aktifitas untuk NF
        AKTIFITAS_LIST.forEach(aktifitas => {
            // Filter data dengan Aktifitas tertentu dan Alokasi = 'NF'
            const filteredDataNF = data.filter(item => 
                item.aktifitas === aktifitas && 
                item.alokasi && 
                item.alokasi.toUpperCase() === 'NF'
            );

            // Filter data dengan Aktifitas tertentu dan Alokasi = 'PS'
            const filteredDataPS = data.filter(item => 
                item.aktifitas === aktifitas && 
                item.alokasi && 
                item.alokasi.toUpperCase() === 'PS'
            );

            // Hitung untuk NF
            let nettoNF = 0;
            let realNF = 0;

            filteredDataNF.forEach(item => {
                // Parse Luas Netto
                const luasNetto = parseNumber(item.luas_netto);
                nettoNF += luasNetto;

                // Parse Real
                const realValue = parseNumber(item.real);
                realNF += realValue;
            });

            const sisaNF = nettoNF - realNF;

            summaryDataNF.push({
                aktifitas,
                netto: nettoNF,
                real: realNF,
                sisa: sisaNF
            });

            totalNettoSumNF += nettoNF;
            totalRealSumNF += realNF;
            totalSisaSumNF += sisaNF;

            // Hitung untuk PS
            let nettoPS = 0;
            let realPS = 0;

            filteredDataPS.forEach(item => {
                // Parse Luas Netto
                const luasNetto = parseNumber(item.luas_netto);
                nettoPS += luasNetto;

                // Parse Real
                const realValue = parseNumber(item.real);
                realPS += realValue;
            });

            const sisaPS = nettoPS - realPS;

            summaryDataPS.push({
                aktifitas,
                netto: nettoPS,
                real: realPS,
                sisa: sisaPS
            });

            totalNettoSumPS += nettoPS;
            totalRealSumPS += realPS;
            totalSisaSumPS += sisaPS;
        });

        // Tambahkan baris TOTAL untuk NF
        summaryDataNF.push({
            aktifitas: "TOTAL",
            netto: totalNettoSumNF,
            real: totalRealSumNF,
            sisa: totalSisaSumNF
        });

        // Tambahkan baris TOTAL untuk PS
        summaryDataPS.push({
            aktifitas: "TOTAL",
            netto: totalNettoSumPS,
            real: totalRealSumPS,
            sisa: totalSisaSumPS
        });

        setDataListNF(summaryDataNF);
        setDataListPS(summaryDataPS);
        
        // Hitung data chart dengan data dari NF dan PS
        const chartDataset = calculateChartData(summaryDataNF, summaryDataPS);
        setChartData(chartDataset);
        
        // Set total untuk ditampilkan
        setTotalNettoNF(totalNettoSumNF);
        setTotalRealNF(totalRealSumNF);
        setTotalSisaNF(totalSisaSumNF);
        
        setTotalNettoPS(totalNettoSumPS);
        setTotalRealPS(totalRealSumPS);
        setTotalSisaPS(totalSisaSumPS);
        
        // Hitung total stok lahan bongkar
        const totalStokLahan = calculateTotalStokLahanBongkar(summaryDataNF, summaryDataPS);
        setTotalStokLahanBongkar({
            freshPine: totalStokLahan.freshPine,
            banana: totalStokLahan.banana
        });
        
        // Panggil callback jika ada
        if (onTotalStokLahanBongkarChange) {
            onTotalStokLahanBongkarChange(totalStokLahan);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (authLoading || loadingData) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Stok Lahan Banana & Fresh Pine Commodity
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Memuat data...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (!user) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Stok Lahan Banana & Fresh Pine Commodity
                </Typography>
                <Box sx={{ p: 2 }}>
                    <Alert severity="error">
                        Anda harus login untuk mengakses halaman ini
                    </Alert>
                </Box>
            </Paper>
        );
    }

    if (dataListNF.length === 0 && dataListPS.length === 0) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Stok Lahan Banana & Fresh Pine Commodity
                    </Typography>

                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": { display: "none" },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": { backgroundColor: "transparent" },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Fresh Pine"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Banana"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 2 ? "primary" : "default"}
                                    variant={activeTab === 2 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 100,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        p: 0,
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        Tidak ada data stok lahan.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Stok Lahan Banana & Fresh Pine Commodity
                    </Typography>

                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Fresh Pine"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Banana"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 2 ? "primary" : "default"}
                                    variant={activeTab === 2 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {/* Container dengan tinggi tetap untuk ketiga tab */}
                
                <Box
                    sx={{
                        height: 350, // Tinggi tetap untuk semua tab
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        overflow: "hidden", // Pastikan konten tidak meluap
                    }}
                >
                    {/* Chart Tab Content */}
                    {activeTab === 0 && (
                        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid size={6}>
                                {/* Chart Fresh Pine */}
                                <Box sx={{ height: "100%", width: "100%", p: 2 }}>
                                    {chartData.length > 0 ? (
                                        <BarChart
                                            dataset={chartData}
                                            yAxis={[
                                                {
                                                    scaleType: "band",
                                                    dataKey: "category",
                                                    width: 220,
                                                    valueFormatter: (value) =>
                                                        value.length > 20 ? `${value.substring(0, 20)}...` : value,
                                                    tickLabelStyle: {
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        textAnchor: "end",
                                                    },
                                                },
                                            ]}
                                            xAxis={[
                                                {
                                                    label: "Luas (Ha)",
                                                    min: 0,
                                                    fontSize: 8,
                                                },
                                            ]}
                                            series={[
                                                {
                                                    dataKey: "NF",
                                                    label: "Fresh Pine",
                                                    valueFormatter,
                                                    color: "#4caf50",
                                                },
                                            ]}
                                            layout="horizontal"
                                            height={300}
                                            margin={{
                                                top: 20,
                                                right: 10,
                                                bottom: 20,
                                                left: -110,
                                            }}
                                            slotProps={{
                                                legend: {
                                                    direction: "row",
                                                    position: { vertical: "top", horizontal: "right" },
                                                    padding: 0,
                                                    itemMarkWidth: 8,
                                                    itemMarkHeight: 8,
                                                    labelStyle: { fontSize: 11 },
                                                    itemGap: 8,
                                                },
                                            }}
                                            sx={{
                                                "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                                "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                                "& .MuiChartsAxis-left .MuiChartsAxis-label": {
                                                    display: "none",
                                                },
                                                "& .MuiChartsAxis-tickLabel": {
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography variant="body1" color="text.secondary">
                                                Tidak ada data untuk chart.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                            <Grid size={6}>
                                {/* Chart Banana */}
                                <Box sx={{ height: "100%", width: "100%", p: 2 }}>
                                    {chartData.length > 0 ? (
                                        <BarChart
                                            dataset={chartData}
                                            yAxis={[
                                                {
                                                    scaleType: "band",
                                                    dataKey: "category",
                                                    width: 220,
                                                    valueFormatter: (value) =>
                                                        value.length > 20 ? `${value.substring(0, 20)}...` : value,
                                                    tickLabelStyle: {
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        textAnchor: "end",
                                                    },
                                                },
                                            ]}
                                            xAxis={[
                                                {
                                                    label: "Luas (Ha)",
                                                    min: 0,
                                                    fontSize: 8,
                                                },
                                            ]}
                                            series={[
                                                {
                                                    dataKey: "PS",
                                                    label: "Banana",
                                                    valueFormatter,
                                                    color: "#fdd835",
                                                },
                                            ]}
                                            layout="horizontal"
                                            height={300}
                                            margin={{
                                                top: 20,
                                                right: 10,
                                                bottom: 20,
                                                left: -110,
                                            }}
                                            slotProps={{
                                                legend: {
                                                    direction: "row",
                                                    position: { vertical: "top", horizontal: "right" },
                                                    padding: 0,
                                                    itemMarkWidth: 8,
                                                    itemMarkHeight: 8,
                                                    labelStyle: { fontSize: 11 },
                                                    itemGap: 8,
                                                },
                                            }}
                                            sx={{
                                                "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                                "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                                                    stroke: "#666666",
                                                },
                                                "& .MuiChartsAxis-left .MuiChartsAxis-label": {
                                                    display: "none",
                                                },
                                                "& .MuiChartsAxis-tickLabel": {
                                                    fontSize: 12,
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                height: "100%",
                                                width: "100%",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Typography variant="body1" color="text.secondary">
                                                Tidak ada data untuk chart.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                        
                    )}

                    {/* Fresh Pine Tab Content */}
                    {activeTab === 1 && (
                        <Box sx={{ height: "100%", width: "100%" }}>
                            {/* Gunakan CustomTableContainer di sini */}
                            <CustomTableContainer
                                sx={{
                                    height: "100%", // Isi penuh container
                                    border: "none", // Hapus border karena sudah ada di parent
                                    borderRadius: 1,
                                }}
                                hoverScrollbar={true}
                                scrollbarHeight="8px"
                                scrollbarWidth="8px"
                                scrollbarThumbColor="#c1c1c1"
                                scrollbarHoverColor="#a8a8a8"
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow className="tr">
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Aktifitas
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Netto
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Real
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Sisa
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dataListNF.map((row, index) => {
                                            const isTotalRow = row.aktifitas === "TOTAL";
                                            
                                            return (
                                                <TableRow 
                                                    key={index}
                                                    hover
                                                    sx={{ 
                                                        backgroundColor: isTotalRow ? '#f0f7ff' : 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: isTotalRow ? '#e3f2fd' : '#f5f5f5'
                                                        }
                                                    }}
                                                >
                                                    {/* Aktifitas */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "primary" : "inherit"}
                                                        >
                                                            {row.aktifitas}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Netto */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "primary" : "inherit"}
                                                        >
                                                            {formatNumberWithSeparator(row.netto)}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Real */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "secondary" : "inherit"}
                                                        >
                                                            {formatNumberWithSeparator(row.real)}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Sisa */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={row.sisa >= 0 ? "success.main" : "error.main"}
                                                        >
                                                            {formatNumberWithSeparator(row.sisa)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CustomTableContainer>
                        </Box>
                    )}

                    {/* Banana Tab Content */}
                    {activeTab === 2 && (
                        <Box sx={{ height: "100%", width: "100%" }}>
                            {/* Gunakan CustomTableContainer di sini */}
                            <CustomTableContainer
                                sx={{
                                    height: "100%", // Isi penuh container
                                    border: "none", // Hapus border karena sudah ada di parent
                                    borderRadius: 1,
                                }}
                                hoverScrollbar={true}
                                scrollbarHeight="8px"
                                scrollbarWidth="8px"
                                scrollbarThumbColor="#c1c1c1"
                                scrollbarHoverColor="#a8a8a8"
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow className="tr">
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Aktifitas
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Netto
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Real
                                            </TableCell>
                                            <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                Sisa
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dataListPS.map((row, index) => {
                                            const isTotalRow = row.aktifitas === "TOTAL";
                                            
                                            return (
                                                <TableRow 
                                                    key={index}
                                                    hover
                                                    sx={{ 
                                                        backgroundColor: isTotalRow ? '#f0faff' : 'inherit',
                                                        '&:hover': {
                                                            backgroundColor: isTotalRow ? '#e1f5fe' : '#f5f5f5'
                                                        }
                                                    }}
                                                >
                                                    {/* Aktifitas */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "primary" : "inherit"}
                                                        >
                                                            {row.aktifitas}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Netto */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "primary" : "inherit"}
                                                        >
                                                            {formatNumberWithSeparator(row.netto)}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Real */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={isTotalRow ? "secondary" : "inherit"}
                                                        >
                                                            {formatNumberWithSeparator(row.real)}
                                                        </Typography>
                                                    </TableCell>
                                                    
                                                    {/* Sisa */}
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        <Typography 
                                                            fontWeight={isTotalRow ? "bold" : "normal"}
                                                            color={row.sisa >= 0 ? "success.main" : "error.main"}
                                                        >
                                                            {formatNumberWithSeparator(row.sisa)}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </CustomTableContainer>
                        </Box>
                    )}
                </Box>

                {/* Info Last Updated di bawah container tab */}
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                    </Typography>
                </Box>
            </Paper>
            
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TotalStokLahanNF;